// Status readouts (GDD §6, §10, §14): treasury with revenue and debt ceiling, the tax dial, the approval meter and
// the war clock. Each is fed from private state or events and writes only what changed.

import { ECONOMY, TAX, TAX_SETTINGS, TRANSPORT_MODE_KEYS, TRANSPORT_ROUTING, type BodyCap, type TaxSetting, type TransportModeKey } from '@taxwar/content'
import { MatchPhase, type PlayerPrivateState, type SimEvent, type TransportCraftCount } from '@taxwar/net'
import { TAX_LABELS } from './catalog'
import { button, el, FractionSlot, TextSlot } from './dom'
import { formatClock, formatMoney, formatMoneyRate, formatMultiplier, formatSignedRate, ticksToWholeSeconds } from './format'
import { phaseOf } from './headlines'

// ------------------------------------------------------------------------------------------------ treasury

const CEILING_NOTE = 'Surplus returned to shareholders'
const SANCTIONED_NOTE = 'Sanctioned'

/**
 * How the HUD names each transport mode's craft (docs/TRANSPORT.md): a Port's boats, an Airbase's planes, and what cuts
 * a backlogged fleet's wait (taxgw-6gcs).
 */
const CRAFT_LABELS: Readonly<Record<TransportModeKey, { readonly fleet: string; readonly moving: string; readonly remedy: string }>> = {
  sea: { fleet: 'Ferries', moving: 'afloat', remedy: 'another Port on that water adds boats' },
  air: { fleet: 'Airlift', moving: 'aloft', remedy: 'another Airbase adds planes' },
}

/** Living bodies over the cap, or the count alone under an unlimited cap. */
function troopsReadout(alive: number, cap: BodyCap): string {
  return cap.kind === 'capped' ? `Troops ${alive}/${cap.bodiesPerPlayer}` : `Troops ${alive}`
}

/** One mode's readout: craft alive against the operational berths' authorised complement, and the wait for one once it counts. */
function craftReadout(mode: TransportModeKey, count: TransportCraftCount, waitSeconds: number): string {
  const label = CRAFT_LABELS[mode]
  const craft = `${label.fleet} ${count.alive} ${label.moving} / ${count.total} authorised`
  return waitSeconds >= TRANSPORT_ROUTING.waitFloorSeconds ? `${craft}, wait ${formatClock(Math.round(waitSeconds))}` : craft
}

/**
 * The transport note (cause + action): groups waiting at or walking to a pickup; groups with neither a ferry nor an
 * airlift route to any objective, fixed by an Airbase on land they can walk to (its planes fly anywhere) or a Port on
 * the water their route must cross (they may already hold a port at their own shore and still lack coverage later
 * on); and each fleet whose wait has passed TRANSPORT_ROUTING.backlogWaitSeconds, cut by another berth of its mode.
 */
function transportNote(state: PlayerPrivateState): string {
  const parts: string[] = []
  if (state.waitingForTransport > 0 || state.boundForTransport > 0) {
    parts.push(`${state.waitingForTransport} ground groups waiting for transport, ${state.boundForTransport} on their way to a pickup`)
  }
  if (state.blockedForTransport > 0) {
    parts.push(`${state.blockedForTransport} ground groups blocked: no ferry or airlift route; build an Airbase they can walk to, or a Port on the water they must cross`)
  }
  for (const mode of TRANSPORT_MODE_KEYS) {
    const wait = state.transportWaitSeconds[mode]
    if (wait >= TRANSPORT_ROUTING.backlogWaitSeconds) parts.push(`${CRAFT_LABELS[mode].fleet} backlog ${formatClock(Math.round(wait))}: ${CRAFT_LABELS[mode].remedy}`)
  }
  return parts.join('; ')
}

export class TreasuryPanel {
  readonly element: HTMLElement
  private readonly money: TextSlot
  private readonly rate: TextSlot
  private readonly ceiling: TextSlot
  private readonly bodies: TextSlot
  private readonly transport: TextSlot
  private readonly transportNote: TextSlot
  private readonly fill: FractionSlot

  constructor() {
    this.element = el('section', 'tw-treasury tw-panel')
    this.element.setAttribute('aria-label', 'Treasury')
    const money = el('span', 'tw-treasury__money tw-money')
    const rate = el('span', 'tw-treasury__rate tw-money')
    const ceiling = el('span', 'tw-treasury__ceiling tw-dim')
    const bodies = el('span', 'tw-treasury__bodies tw-dim')
    const transport = el('span', 'tw-treasury__transport tw-dim')
    const transportNote = el('span', 'tw-treasury__transport-note tw-dim')
    const gauge = el('div', 'tw-gauge tw-treasury__gauge')
    gauge.append(el('span', 'tw-gauge__fill'))
    const row = el('div', 'tw-treasury__row')
    row.append(money, rate)
    const notes = el('div', 'tw-treasury__notes')
    notes.append(el('span', 'tw-badge tw-treasury__capped', CEILING_NOTE), el('span', 'tw-badge tw-treasury__sanctioned', SANCTIONED_NOTE))
    const meta = el('div', 'tw-treasury__row')
    meta.append(ceiling, bodies, transport)
    this.element.append(el('h2', 'tw-heading', 'Treasury'), row, gauge, meta, transportNote, notes)
    this.money = new TextSlot(money)
    this.rate = new TextSlot(rate)
    this.ceiling = new TextSlot(ceiling)
    this.bodies = new TextSlot(bodies)
    this.transport = new TextSlot(transport)
    this.transportNote = new TextSlot(transportNote)
    this.fill = new FractionSlot(gauge, '--tw-fill')
  }

  update(state: PlayerPrivateState): void {
    this.money.set(formatMoney(state.money))
    this.rate.set(`+${formatMoneyRate(state.revenuePerSec)}`)
    this.ceiling.set(`Debt ceiling ${formatMoney(state.debtCeiling)}`)
    this.bodies.set(troopsReadout(state.bodiesAlive, state.bodyCap))
    // A mode's alive count can exceed its authorised complement while orphan trips finish, so the labels say
    // afloat/aloft, not available.
    this.transport.set(TRANSPORT_MODE_KEYS.map((mode) => craftReadout(mode, state.transportCraft[mode], state.transportWaitSeconds[mode])).join(' · '))
    this.transportNote.set(transportNote(state))
    this.fill.set(state.money / state.debtCeiling)
    this.element.classList.toggle('is-capped', state.money >= state.debtCeiling)
    this.element.classList.toggle('is-sanctioned', state.sanctionedUntilTick > state.tick)
  }
}

// ------------------------------------------------------------------------------------------------ tax dial

export class TaxDial {
  readonly element: HTMLElement
  private readonly options: ReadonlyMap<TaxSetting, HTMLButtonElement>

  constructor(onSelect: (setting: TaxSetting) => void, signal: AbortSignal) {
    this.element = el('section', 'tw-tax tw-panel')
    const group = el('div', 'tw-tax__options')
    group.setAttribute('role', 'radiogroup')
    group.setAttribute('aria-label', 'Tax rate')
    const options = new Map<TaxSetting, HTMLButtonElement>()
    for (const setting of TAX_SETTINGS) {
      const option = button('tw-tax__option', '')
      option.setAttribute('role', 'radio')
      option.setAttribute('aria-checked', 'false')
      const def = TAX[setting]
      option.append(
        el('span', 'tw-tax__label', TAX_LABELS[setting]),
        el('span', 'tw-tax__detail', `${formatMultiplier(def.revenueMultiplier)} ${formatSignedRate(def.approvalPerSec)}`),
      )
      option.addEventListener('click', () => onSelect(setting), { signal })
      options.set(setting, option)
      group.append(option)
    }
    this.options = options
    this.element.append(el('h2', 'tw-heading', 'Tax rate'), group)
  }

  setCurrent(current: TaxSetting): void {
    for (const [setting, option] of this.options) {
      const checked = setting === current
      option.classList.toggle('is-current', checked)
      option.setAttribute('aria-checked', checked ? 'true' : 'false')
    }
  }
}

// ------------------------------------------------------------------------------------------------ approval

/** GDD §6: approval runs 0–100; 0 is regime change. */
const APPROVAL_SCALE_MAX = 100
const PERCENT = 100

type ApprovalBand = Extract<SimEvent, { readonly kind: 'approvalThreshold' }>['band']

const BAND_LABELS: Readonly<Record<ApprovalBand, string>> = {
  high: 'Adored',
  normal: 'Tolerated',
  low: 'Protests',
}
const BAND_CLASSES: Readonly<Record<ApprovalBand, string>> = {
  high: 'is-high',
  normal: 'is-normal',
  low: 'is-low',
}
const BANDS = Object.keys(BAND_LABELS) as readonly ApprovalBand[]

/** Same inclusive band edges as the sim (content ECONOMY thresholds). */
function approvalBand(approval: number): ApprovalBand {
  if (approval >= ECONOMY.highApprovalThreshold) return 'high'
  if (approval <= ECONOMY.lowApprovalThreshold) return 'low'
  return 'normal'
}

export class ApprovalMeter {
  readonly element: HTMLElement
  private readonly value: TextSlot
  private readonly band: TextSlot
  private readonly fill: FractionSlot
  private readonly gauge: HTMLElement
  private shownValue: number | null = null
  private shownBand: ApprovalBand | null = null

  constructor() {
    this.element = el('section', 'tw-approval tw-panel')
    this.element.setAttribute('aria-label', 'Approval')
    const value = el('span', 'tw-approval__value')
    const band = el('span', 'tw-approval__band')
    const gauge = el('div', 'tw-gauge tw-approval__gauge')
    gauge.setAttribute('role', 'meter')
    gauge.setAttribute('aria-valuemin', '0')
    gauge.setAttribute('aria-valuemax', String(APPROVAL_SCALE_MAX))
    gauge.style.setProperty('--tw-low-mark', `${(ECONOMY.lowApprovalThreshold / APPROVAL_SCALE_MAX) * PERCENT}%`)
    gauge.style.setProperty('--tw-high-mark', `${(ECONOMY.highApprovalThreshold / APPROVAL_SCALE_MAX) * PERCENT}%`)
    gauge.append(el('span', 'tw-gauge__fill'), el('span', 'tw-approval__mark tw-approval__mark--low'), el('span', 'tw-approval__mark tw-approval__mark--high'))
    const row = el('div', 'tw-approval__row')
    row.append(value, band)
    this.element.append(el('h2', 'tw-heading', 'Approval'), row, gauge)
    this.value = new TextSlot(value)
    this.band = new TextSlot(band)
    this.fill = new FractionSlot(gauge, '--tw-fill')
    this.gauge = gauge
  }

  update(approval: number): void {
    const shown = Math.round(approval)
    if (shown !== this.shownValue) {
      this.shownValue = shown
      const text = String(shown)
      this.value.set(text)
      this.gauge.setAttribute('aria-valuenow', text)
    }
    const band = approvalBand(approval)
    if (band !== this.shownBand) {
      this.shownBand = band
      this.band.set(BAND_LABELS[band])
      for (const key of BANDS) this.element.classList.toggle(BAND_CLASSES[key], key === band)
    }
    this.fill.set(approval / APPROVAL_SCALE_MAX)
  }
}

// ------------------------------------------------------------------------------------------------ war clock

/** Phases whose deadline (PlayerPrivateState.phaseEndTick) is a war clock; the others show no time. */
const PHASE_HAS_CLOCK: Readonly<Record<MatchPhase, boolean>> = {
  [MatchPhase.setup]: false,
  [MatchPhase.regulation]: true,
  [MatchPhase.overtime]: true,
  [MatchPhase.ended]: false,
}

const PHASE_LABELS: Readonly<Record<MatchPhase, string>> = {
  [MatchPhase.setup]: 'Mobilizing',
  [MatchPhase.regulation]: 'Regulation',
  [MatchPhase.overtime]: 'Overtime · sudden death',
  [MatchPhase.ended]: 'Ceasefire',
}

const NO_CLOCK_TEXT = '-:--'
/** The clock turns to warning colours for the last stretch of a phase. */
const CLOCK_WARNING_SECONDS = 30

/** Phase and time left, both straight from the sim's private state. */
export class WarClock {
  readonly element: HTMLElement
  private readonly label: TextSlot
  private readonly time: TextSlot

  constructor() {
    this.element = el('section', 'tw-clock tw-panel')
    this.element.setAttribute('aria-label', 'War clock')
    const time = el('span', 'tw-clock__time', NO_CLOCK_TEXT)
    const label = el('span', 'tw-clock__label')
    this.element.append(time, label)
    this.label = new TextSlot(label)
    this.time = new TextSlot(time)
  }

  update(state: PlayerPrivateState): void {
    const phase = phaseOf(state.phase)
    this.label.set(PHASE_LABELS[phase])
    const clocked = PHASE_HAS_CLOCK[phase]
    const seconds = ticksToWholeSeconds(state.phaseEndTick - state.tick)
    this.time.set(clocked ? formatClock(seconds) : NO_CLOCK_TEXT)
    this.element.classList.toggle('is-overtime', phase === MatchPhase.overtime)
    this.element.classList.toggle('is-warning', clocked && seconds <= CLOCK_WARNING_SECONDS)
  }
}
